"use client"
import React, { useEffect, useRef, useState } from "react"
import sanitizeHtml from 'sanitize-html'
import { gsap } from "gsap"
import { useGSAP } from "@gsap/react"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { SplitText } from "gsap/SplitText"
import Link from "next/link"
import { IconArrowRight } from "@tabler/icons-react"

interface VerticalCardProps {
  content: { [key: string]: string }
  withExcerpt?: boolean
  className?: string
  index: number
}

gsap.registerPlugin(ScrollTrigger, useGSAP, SplitText)

export default function VerticalCard({ content, withExcerpt = false, className = "", index }: VerticalCardProps) {
  const cardRef = useRef<HTMLElement>(null)
  const imageRef = useRef(null)
  const textRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  
  let repo
  switch (content?.type) {
    case "post":
      repo = "insights"
      break
    case "work":
      repo = "work"
      break
    default:
      repo = ""
  }

  const { excerpt, slug, title, _embedded } = content

  // Use GSAP's useGSAP for better cleanup and context management
  useGSAP(() => {
    if (!cardRef.current || !isVisible) return
    
    const isOdd = index % 2 !== 0
    const delay = isOdd ? 0.1 : 0

    // IMAGE ANIMATION
    if (imageRef.current) {
      const image = imageRef.current

      gsap.fromTo(image, 
        {
          scale: 1.05,
          clipPath: "inset(100% 0% 0% 0%)",
          opacity: 0
        },
        {
          clipPath: "inset(0% 0% 0% 0%)",
          scale: 1,
          opacity: 1,
          duration: 1,
          delay,
          ease: "expo.out",
          scrollTrigger: {
            trigger: image,
            start: "top 95%",
            toggleActions: "play none none none",
            once: true
          }
        }
      )
    }

    // TEXT ANIMATIONS
    const h2El = textRef.current?.querySelector("h2")
    const excerptEl = textRef.current?.querySelector(".work-excerpt")

    if (h2El) {
      const splitTitle = new SplitText(h2El, {
        type: "words",
        mask: "words",
        autoSplit: false
      })

      gsap.fromTo(
        splitTitle.words,
        {
          yPercent: 90,
          opacity: 0
        },
        {
          yPercent: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power4.out",
          stagger: 0.03,
          delay: delay + 0.2,
          scrollTrigger: {
            trigger: h2El,
            start: "top 95%",
            toggleActions: "play none none none",
            once: true,
          }
        }
      )
    }

    if (excerptEl && withExcerpt) {
      const splitExcerpt = new SplitText(excerptEl, {
        type: "lines",
        mask: "lines"
      })

      gsap.fromTo(
        splitExcerpt.lines,
        {
          y: 50,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.75,
          stagger: 0.02,
          ease: "power2.out",
          delay: delay + 0.4,
          scrollTrigger: {
            trigger: excerptEl,
            start: "top 95%",
            toggleActions: "play none none none",
            once: true,
          }
        }
      )
    }
  }, [isVisible, index, title?.rendered, excerpt?.rendered])

  // Improved intersection observer with higher threshold and immediate cleanup
  useEffect(() => {
    if (!cardRef.current) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          // Once visible, no need to keep observing
          observer.disconnect()
        }
      },
      { 
        threshold: 0.05,
        rootMargin: "0px 0px 1000px 0px" // Starts loading earlier before it comes into view
      }
    )

    observer.observe(cardRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <article ref={cardRef} className={`vertical-card ${className} overflow-hidden rounded-xl p-4 duration-200 mb-12 md:mb-16 lg:mb-20`}>
      <Link href={`/${repo}/${slug}`} className="block group transition-all duration-300 ease-in-out hover:-translate-y-1" data-target="link">
        <div className="flex flex-col">
          <figure className="relative aspect-[4/3] w-full h-full mb-6 md:mb-8 overflow-hidden group-hover:scale-[1.03] duration-200 transition-transform ease-linear">
              <img ref={imageRef} src={_embedded["wp:featuredmedia"][0].source_url} alt={_embedded["wp:featuredmedia"][0].alt_text} className="object-cover rounded-2xl w-full h-full" style={{ opacity: 0, viewTransitionName: `{repo}-${slug}` }} />
          </figure>
        </div>
        <div ref={textRef} className="overflow-hidden">
          <div className="flex items-center group-hover:gap-2">
            <IconArrowRight size={10} className="opacity-0 scale-75 transition-all duration-300 group-hover:opacity-100 group-hover:scale-100 size-0 group-hover:size-5" />
            <h2 className="text-3xl md:text-4xl font-semibold pb-0 mb-0 leading-tight" dangerouslySetInnerHTML={{ __html: sanitizeHtml(title?.rendered) }} />
          </div>
          {withExcerpt && <div className="work-excerpt" dangerouslySetInnerHTML={{ __html: sanitizeHtml(excerpt?.rendered) }} />}
        </div>
      </Link>
    </article>
  )
}